/** 建议零售价展示辅助 */

import {
  RRP_AUDIT_STATE_LABELS,
  RRP_MATERIAL_REQUIREMENT_LABELS,
  RRP_SKC_STATUS_LABELS,
} from '@/shein-platform/constants/sheinApiCatalog';

export function rrpSkcStatusLabel(code) {
  if (code === undefined || code === null || code === '') return '—';
  return RRP_SKC_STATUS_LABELS[code] || RRP_SKC_STATUS_LABELS[Number(code)] || String(code);
}

export function rrpAuditStateLabel(code) {
  if (code === undefined || code === null || code === '') return '—';
  return RRP_AUDIT_STATE_LABELS[code] || RRP_AUDIT_STATE_LABELS[Number(code)] || String(code);
}

export function rrpAuditModifier(code) {
  const n = Number(code);
  if (n === 1) return 'pending';
  if (n === 2 || n === 5) return 'success';
  if (n === 3 || n === 6) return 'failed';
  return 'pending';
}

/** SKC 当前建议零售价状态色 */
export function rrpSkcStatusModifier(code) {
  const n = Number(code);
  if (n === 1 || n === 5) return 'pending';
  if (n === 4) return 'success';
  if (n === 2 || n === 3 || n === 6 || n === 7) return 'failed';
  return 'pending';
}

export function rrpMaterialRequirementLabel(code) {
  if (code === undefined || code === null || code === '') return '—';
  return RRP_MATERIAL_REQUIREMENT_LABELS[code]
    || RRP_MATERIAL_REQUIREMENT_LABELS[Number(code)]
    || String(code);
}

export function extractRrpAuditList(info) {
  if (!info) return [];
  if (Array.isArray(info.data)) return info.data;
  if (Array.isArray(info)) return info;
  return [];
}

export function extractRrpAuditTotal(info, fallback = 0) {
  if (!info || typeof info !== 'object') return fallback;
  const n = Number(info?.meta?.count ?? info.count ?? info.total);
  return Number.isFinite(n) ? n : fallback;
}

export function extractRrpSkcList(info) {
  if (!info) return [];
  if (Array.isArray(info.skc_rrp_list)) return info.skc_rrp_list;
  if (Array.isArray(info.skcRrpList)) return info.skcRrpList;
  return [];
}

export function extractRrpSkcTotal(info, fallback = 0) {
  if (!info || typeof info !== 'object') return fallback;
  const n = Number(info.count ?? info.total ?? info?.meta?.count);
  return Number.isFinite(n) ? n : fallback;
}

export function formatMsTime(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '—';
  try {
    return new Date(n).toLocaleString();
  } catch {
    return String(ms);
  }
}

/** 审核记录时间：平台可能返回字符串或毫秒时间戳 */
export function formatAuditTime(value) {
  if (value === undefined || value === null || value === '') return '—';
  const raw = String(value).trim();
  if (/^\d{10,13}$/.test(raw)) {
    const n = Number(raw);
    return formatMsTime(raw.length === 10 ? n * 1000 : n);
  }
  return raw;
}

export function formatRrpMoney(amount, currency) {
  if (amount === undefined || amount === null || amount === '') return '—';
  const cur = currency ? `${currency} ` : '';
  return `${cur}${amount}`;
}

/** 审核记录 / 当前价：汇总 SKU 站点建议零售价（一行一个站点） */
export function summarizeSkuRrpInfoList(skuList, { max = 0 } = {}) {
  const list = Array.isArray(skuList) ? skuList : [];
  const lines = [];
  for (const sku of list) {
    const sites = Array.isArray(sku?.site_rrp_info_list)
      ? sku.site_rrp_info_list
      : (sku?.site || sku?.currency || sku?.suggested_retail_price != null ? [sku] : []);
    for (const site of sites) {
      const price = site?.suggested_retail_price;
      if (price === undefined || price === null || price === '') continue;
      const siteName = String(site?.site || '').trim();
      const money = formatRrpMoney(price, site?.currency);
      lines.push(siteName ? `${siteName}: ${money}` : money);
      if (max > 0 && lines.length >= max) {
        return {
          lines: [...lines.slice(0, max - 1), `${lines[max - 1]}…`],
          text: `${lines.slice(0, max).join('\n')}…`,
          count: lines.length,
          truncated: true,
        };
      }
    }
  }
  return {
    lines,
    text: lines.length ? lines.join('\n') : '—',
    count: lines.length,
    truncated: false,
  };
}

export function pickRrpProductTitle(row) {
  return row?.product_title
    || row?.productTitle
    || row?.sale_attribute_value
    || row?.skc_name
    || '—';
}

export function pickRrpProductImage(row) {
  return row?.image_url || row?.imageUrl || row?.mainPicUrl || '';
}

export function pickRrpCategory(row) {
  return row?.category_name || row?.categoryName || '';
}

export function pickRrpBrandCode(row) {
  return String(row?.brand_code || row?.brandCode || '').trim();
}

/** 联调示例 SKC（本地 seed，平台无对应数据） */
export function isRrpDemoSkc(skcName) {
  return String(skcName || '').trim().startsWith('DEMO-SKC-RRP-');
}

export function pickRrpSkcName(row) {
  return String(row?.skc_name || row?.skcName || '').trim();
}

/** 是否已有可编辑的 SKU/站点建议零售价行 */
export function hasRrpSkuPriceRows(row) {
  const list = Array.isArray(row?.sku_rrp_info_list)
    ? row.sku_rrp_info_list
    : (Array.isArray(row?.skuRrpInfoList) ? row.skuRrpInfoList : []);
  for (const sku of list) {
    const sites = Array.isArray(sku?.site_rrp_info_list)
      ? sku.site_rrp_info_list
      : (Array.isArray(sku?.siteRrpInfoList) ? sku.siteRrpInfoList : []);
    if (sites.length > 0) return true;
    if (sku?.suggested_retail_price != null && String(sku.suggested_retail_price).trim() !== '') {
      return true;
    }
  }
  return false;
}

function pickRrpSkuList(row) {
  if (!row || typeof row !== 'object') return [];
  if (Array.isArray(row.sku_rrp_info_list)) return row.sku_rrp_info_list;
  if (Array.isArray(row.skuRrpInfoList)) return row.skuRrpInfoList;
  return [];
}

/**
 * 合并编辑资料：平台/缓存查询结果为主，列表行补展示字段；
 * 价格行优先取「有 SKU 站点价」的一侧，避免空 live 结果冲掉本地完整数据。
 */
export function mergeRrpSkcEditRow(apiRow, listRow = null) {
  const base = listRow && typeof listRow === 'object' ? listRow : {};
  const api = apiRow && typeof apiRow === 'object' ? apiRow : {};
  const apiList = pickRrpSkuList(api);
  const baseList = pickRrpSkuList(base);
  const preferApiPrices = hasRrpSkuPriceRows(api);
  const preferBasePrices = !preferApiPrices && hasRrpSkuPriceRows(base);
  return {
    ...base,
    ...api,
    product_title: api.product_title || api.productTitle || base.product_title || base.productTitle || '',
    image_url: api.image_url || api.imageUrl || base.image_url || base.imageUrl || '',
    category_name: api.category_name || api.categoryName || base.category_name || base.categoryName || '',
    sale_attribute_value: api.sale_attribute_value || base.sale_attribute_value || '',
    brand_code: pickRrpBrandCode(api) || pickRrpBrandCode(base),
    sku_rrp_info_list: preferApiPrices
      ? apiList
      : (preferBasePrices ? baseList : (apiList.length ? apiList : baseList)),
  };
}

export function normalizeRrpProof(proof) {
  if (!proof || typeof proof !== 'object') {
    return { attachments: [], proof_link: '' };
  }
  const attachments = Array.isArray(proof.attachments)
    ? proof.attachments.map((u) => String(u || '').trim()).filter(Boolean).slice(0, 6)
    : [];
  const proof_link = String(proof.proof_link || proof.proofLink || '').trim().slice(0, 1000);
  return { attachments, proof_link };
}

export function rrpProofHasContent(proof) {
  const p = normalizeRrpProof(proof);
  return p.attachments.length > 0 || Boolean(p.proof_link);
}

export function toRrpProofPayload(attachments, proofLink) {
  return normalizeRrpProof({ attachments, proof_link: proofLink });
}

export function formatRrpFilledPrice(n) {
  if (!Number.isFinite(n)) return '';
  const rounded = Math.round(n * 100) / 100;
  return String(rounded);
}

/** 空、非数字、0 都视为无建议价（0 加价仍是 0） */
export function isRrpSuggestedPriceEmpty(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return true;
  const n = Number(raw);
  return !Number.isFinite(n) || n === 0;
}

/**
 * 按建议价资料库自动填空价 / 0 价站点，并补空的类型一/二证明材料。
 * 空价：有固定底价则「底价 × (1+加价%)」；已有价则「现有价 × (1+加价%)」。
 */
export function applyRrpAutoFill(rows, template) {
  const payload = template?.payload && typeof template.payload === 'object' ? template.payload : {};
  const markupRaw = payload.markupPercent ?? template?.markupPercent;
  const markup = markupRaw != null && markupRaw !== '' ? Number(markupRaw) : NaN;
  const fixedRaw = payload.fixedPrice ?? template?.fixedPrice;
  const fixedPrice = fixedRaw != null && fixedRaw !== '' ? Number(fixedRaw) : NaN;
  const hasFixed = Number.isFinite(fixedPrice) && fixedPrice > 0;
  const hasMarkup = Number.isFinite(markup);
  const typeOne = normalizeRrpProof(payload.type_one_proof || payload.typeOneProof);
  const typeTwo = normalizeRrpProof(payload.type_two_proof || payload.typeTwoProof);
  const hasTypeOne = rrpProofHasContent(typeOne);
  const hasTypeTwo = rrpProofHasContent(typeTwo);

  let filled = false;
  const next = (Array.isArray(rows) ? rows : []).map((row) => {
    let changed = false;
    const out = { ...row };
    const empty = isRrpSuggestedPriceEmpty(row?.suggested_retail_price);
    if (empty && hasFixed) {
      const price = hasMarkup ? fixedPrice * (1 + markup / 100) : fixedPrice;
      out.suggested_retail_price = formatRrpFilledPrice(price);
      changed = true;
    } else if (!empty && hasMarkup) {
      out.suggested_retail_price = formatRrpFilledPrice(Number(row.suggested_retail_price) * (1 + markup / 100));
      changed = true;
    }
    if (hasTypeOne && !rrpProofHasContent({
      attachments: row?.type_one_attachments,
      proof_link: row?.type_one_link,
    })) {
      out.type_one_attachments = [...typeOne.attachments];
      out.type_one_link = typeOne.proof_link;
      changed = true;
    }
    if (hasTypeTwo && !rrpProofHasContent({
      attachments: row?.type_two_attachments,
      proof_link: row?.type_two_link,
    })) {
      out.type_two_attachments = [...typeTwo.attachments];
      out.type_two_link = typeTwo.proof_link;
      changed = true;
    }
    if (!changed) return row;
    filled = true;
    return out;
  });
  return { rows: next, filled };
}

/**
 * 从 sku_rrp_info_list 收集类型一/类型二证明材料（按 SKU + 站点）。
 * @returns {{ hasProof: boolean, items: Array<{ sku_code, site, type_one_proof, type_two_proof }> }}
 */
export function collectRrpProofMaterials(skuList) {
  const list = Array.isArray(skuList) ? skuList : [];
  const items = [];
  for (const sku of list) {
    const skuCode = String(sku?.sku_code || sku?.skuCode || '').trim();
    const sites = Array.isArray(sku?.site_rrp_info_list)
      ? sku.site_rrp_info_list
      : (sku?.site || sku?.type_one_proof || sku?.type_two_proof ? [sku] : []);
    for (const site of sites) {
      const typeOne = normalizeRrpProof(site?.type_one_proof || site?.typeOneProof);
      const typeTwo = normalizeRrpProof(site?.type_two_proof || site?.typeTwoProof);
      if (!rrpProofHasContent(typeOne) && !rrpProofHasContent(typeTwo)) continue;
      items.push({
        sku_code: skuCode || '—',
        site: String(site?.site || '').trim() || '不分站点',
        type_one_proof: typeOne,
        type_two_proof: typeTwo,
      });
    }
  }
  return { hasProof: items.length > 0, items };
}
